import { icons } from 'common'
import { Link } from 'components/Atoms/Link'
import { API } from 'constants/apiEndPoints'
import { ASYNC_STATUS_ID } from 'constants/index'
import { useYoloToast } from 'lib/yoloToasts/useYoloToast'
import { isEmpty } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { currencyFormatter } from 'utils'
import { useAPI } from 'utils/hooks/useAPI'
import { isAddressesEqual } from 'utils/wallet/addresses'

type followee = {
  parentAddress: string
  maxAmount: number
  avatar: string
  username: string
}
type follower = {
  childAddress: string
  maxAmount: number
  avatar: string
  username: string
}

type followees = followee[]
type followers = {
  followers: follower[]
  totalFeesInfo: {
    totalFeesEarned?: number
    parentFeeRateBp?: number
  }
}

const UserToastCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  color: #000;
  span {
    color: inherit;
    display: flex;
    gap: 8px;
    align-items: center;
  }
  div {
    color: inherit;
  }
  a {
    color: inherit !important;
  }
  p {
    color: inherit;
    margin: 0 0 5px 0;
    white-space: break-spaces;
  }
  strong {
    color: inherit;
    font-weight: 600;
  }
`

const followingToast = (username: string, avatar: string, maxAmount: number) => ({
  id: 'genericToast',
  props: {
    message: {
      icon: icons.clone_bid_icon_toast,
      title: "You are now following and cloning this user's bids",
      subtitle: (
        <UserToastCard>
          <span>
            <img alt='avatar' src={avatar} width='24' height='24' /> {username}
          </span>
          <p>
            <strong>Pro tip: </strong>You can find the list of users that you're currently bid cloning in your user
            profile linked at the top of the games panel.
          </p>
        </UserToastCard>
      ),
    },
  },
})
const followingErrorToast = (username: string, errorMsg: string) => ({
  id: 'errorToast',
  props: {
    message: {
      title: `Something went wrong when try to follow ${username}`,
      subtitle: errorMsg,
    },
  },
})

const unfollowingToast = (username: string, avatar: string) => ({
  id: 'genericToast',
  props: {
    message: {
      icon: icons.clone_bid_icon_toast_stopped,
      title: "You have stopped cloning this user's bids",
      subtitle: (
        <UserToastCard>
          <p>
            Please note, any bids that are cloned will remain open until <br /> you close them manually.
          </p>
          <span>
            <img alt='avatar' src={avatar} width='24' height='24' /> {username}
          </span>
        </UserToastCard>
      ),
    },
  },
})
const unfollowingErrorToast = (username: string, errorMsg: string) => ({
  id: 'errorToast',
  props: {
    message: {
      title: `Something went wrong when try to unfollow ${username}`,
      subtitle: errorMsg,
    },
  },
})

export const useLvgCloneBids = (): any => {
  const { yToast } = useYoloToast()
  const [followees, setFollowees] = useState<followees>([])
  const [followersData, setFollowersData] = useState<followers>({ followers: [], totalFeesInfo: {} })
  const [isPending, setIsPending] = useState<string>('')
  const [isFollowersPending, setIsFollowersPending] = useState<boolean>(false)

  //Manage the followees list
  const [followeesState, sendFolloweesQuery, hasFolloweesState, resetFolloweesStatus] = useAPI(
    API.LVG_CLONE_BIDS_FOLLOWS,
    {
      queryType: 'get',
      withJwt: true,
      controlled: true,
    }
  )

  useEffect(() => {
    setIsPending('all')
    sendFolloweesQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (hasFolloweesState(ASYNC_STATUS_ID.ERROR)) setIsPending('')
    if (hasFolloweesState(ASYNC_STATUS_ID.CONFIRMED)) {
      setFollowees(followeesState?.data?.array || [])
      setIsPending('')
    }
  }, [followeesState, hasFolloweesState])

  // Get the followers list
  const [followersState, sendFollowersQuery, hasFollowersState, resetFollowersStatus] = useAPI(
    API.LVG_CLONE_BIDS_FOLLOWERS,
    {
      queryType: 'get',
      withJwt: true,
      controlled: true,
    }
  )

  useEffect(() => {
    if (hasFollowersState(ASYNC_STATUS_ID.ERROR)) setIsFollowersPending(false)

    if (hasFollowersState(ASYNC_STATUS_ID.CONFIRMED)) {
      const followersData = followersState.data?.followerProfileDetails || []
      const totalFeesInfo = followersState.data?.totalFeesInfo || {}
      console.log('ACZ: followersData1', { followersData: followersState.data })
      setFollowersData({ followers: followersData, totalFeesInfo })
      setIsFollowersPending(false)
    }
  }, [followersState, hasFollowersState])

  const getFollowers = useCallback(() => {
    setIsFollowersPending(true)
    sendFollowersQuery()
  }, [sendFollowersQuery])

  //Follow a user
  const [followState, sendFollowQuery, hasFollowState, resetFollowStatus] = useAPI(API.LVG_CLONE_BIDS_FOLLOW, {
    queryType: 'post',
    withJwt: true,
    controlled: true,
  })

  useEffect(() => {
    if (hasFollowState(ASYNC_STATUS_ID.ERROR)) {
      const username = followState.queries.at(0).tunnel?.username
      const errorMsg = followState.status?.message
      yToast(followingErrorToast(username, errorMsg))
      setIsPending('')
      resetFollowStatus()
    }
    if (hasFollowState(ASYNC_STATUS_ID.CONFIRMED)) {
      const followees = followState.data?.array || []
      const parentAddress = followState.data?.tunnel?.parentAddress
      const followee = followees.find((followee: followee) => isAddressesEqual(followee.parentAddress, parentAddress))
      setFollowees((prev) => followees)
      yToast(followingToast(followee.username, followee.avatar, followee.maxAmount))
      setIsPending('')
      resetFollowStatus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followState?.id, hasFollowState])

  const followUser = useCallback((parentAddress: string, username: string) => {
    setIsPending('all')
    sendFollowQuery({ params: { parentAddress }, tunnel: { parentAddress, username } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //Unfollow a user
  const [unfollowState, sendUnfollowQuery, hasUnfollowState, resetUnfollowStatus] = useAPI(
    API.LVG_CLONE_BIDS_UNFOLLOW,
    {
      queryType: 'post',
      withJwt: true,
      controlled: true,
    }
  )

  useEffect(() => {
    if (hasUnfollowState(ASYNC_STATUS_ID.ERROR)) {
      const username = unfollowState.queries.at(0).tunnel?.followee?.username
      const errorMsg = unfollowState.status?.message
      console.log('ACZ: followState', { unfollowState })
      yToast(unfollowingErrorToast(username, errorMsg))
      setIsPending('')
      resetFollowStatus()
    }
    if (hasUnfollowState(ASYNC_STATUS_ID.CONFIRMED)) {
      const followees = unfollowState.data?.array || []
      const followee = unfollowState.data?.tunnel?.followee
      setFollowees(followees)
      yToast(unfollowingToast(followee.username, followee.avatar))
      setIsPending('')
      resetUnfollowStatus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unfollowState?.id, hasUnfollowState])

  const unfollowUser = useCallback(
    (parentAddress: string) => {
      setIsPending(parentAddress)
      const followee = followees.find((followee: followee) => isAddressesEqual(followee.parentAddress, parentAddress))
      sendUnfollowQuery({ params: { parentAddress }, tunnel: { followee } })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [followees]
  )

  //Set Follow fees
  const [setFollowFeesState, sendSetFollowFeesQuery, hasSetFollowFeesState, resetSetFollowFeesStatus] = useAPI(
    API.LVG_CLONE_BIDS_SET_FOLLOW_FEE,
    {
      queryType: 'post',
      withJwt: true,
      controlled: true,
    }
  )

  useEffect(() => {
    if (hasSetFollowFeesState(ASYNC_STATUS_ID.ERROR)) {
      const errorMsg = setFollowFeesState.status?.message
      console.log('ACZ: errorMsg', { errorMsg })
    }
    if (hasSetFollowFeesState(ASYNC_STATUS_ID.CONFIRMED)) {
      const response = setFollowFeesState || []
      console.log('ACZ: response', { response })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFollowFeesState?.id, hasSetFollowFeesState])

  const setFollowFees = useCallback(
    (feePercentage: number) => {
      const feeRateBp = feePercentage * 100
      console.log('ACZ: feeBasisPoints', { feeRateBp })
      sendSetFollowFeesQuery({ params: { feeRateBp } })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  //Parent fee rate
  const [parentFeeRateState, parentFeeRateQuery, hasParentFeeRateState] = useAPI(API.LVG_CLONE_BIDS_FOLLOW_FEE, {
    queryType: 'get',
    withJwt: true,
    controlled: true,
  })

  const [parentFeeRate, setParentFeeRate] = useState(0)

  useEffect(() => {
    if (hasParentFeeRateState(ASYNC_STATUS_ID.ERROR)) {
      const errorMsg = parentFeeRateState.status?.message
      console.log('ACZ: errorMsg', { errorMsg })
    }
    if (hasParentFeeRateState(ASYNC_STATUS_ID.CONFIRMED)) {
      const response = parentFeeRateState || []
      setParentFeeRate(response.data?.feeRate)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentFeeRateState?.id, hasParentFeeRateState])

  const getParentFeeRate = useCallback((parentAddress: string) => {
    parentFeeRateQuery({ params: { parentAddress } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //Helpers functions

  const isFollowing = useCallback(
    (address: string) => {
      if (isEmpty(followees)) return false
      const isFollowing = followees.find((followee: followee) => isAddressesEqual(followee.parentAddress, address))
      return !!isFollowing
    },
    [followees]
  )

  const getFollowee = useCallback(
    (address: string) => {
      if (isEmpty(followees)) return null
      const followee = followees.find((followee: followee) => isAddressesEqual(followee.parentAddress, address))
      return followee
    },
    [followees]
  )

  return {
    followees,
    isPending,
    followersData,
    parentFeeRate,
    getParentFeeRate,
    isFollowersPending,
    isFollowing,
    getFollowee,
    getFollowers,
    setFollowFees,
    followUser,
    unfollowUser,
  }
}
